import { css } from '@linaria/core';
import type React from 'react';
import { forwardRef } from 'react';

import { color } from '@creditornot/cb-ingredients/design-tokens';
import {
  assignInlineCssVars,
  clsx,
  createAttributeCssVarDefinitions,
  createCssVars,
} from '@creditornot/cb-utils';

// Notes about the implementation:
// You may wonder why the structure of the spinner is so complicated.
// The reason is: it's not possible to render the spinner completely on the GPU otherwise.
// It seems that e.g. Chrome can animate div elements with GPU but not svg elements.
// Moreover, there can be only one animation per element and it's children.
// We want to render the spinner off the UI thread to keep it spinning while the CPU
// is busy, e.g. when parsing and evaluating a dynamically loaded JS bundle.

export const sizeOptions = ['tiny', 'small', 'medium', 'large', 'extraLarge', 'huge'] as const;
export const colorOptions = ['blue', 'grey'] as const;

export type Size = (typeof sizeOptions)[number];
export type Color = 'blue' | 'grey';

type StyleParams = { color?: string; size?: string };

export interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * @default 'blue'
   */
  color?: Color;
  size: Size;
  /**
   * Optional configuration to override some stylistic parameters.
   *
   * `styleParams` are intended to be used only in the rare cases when you have a special
   * need to customize the component. It is _not_ intended for component instance specific
   * customization to adjust to layout requirements, for example. It is recommended that instead
   * of setting `styleParams` on every instance, you create a proxy component that sets the
   * correct overrides for all instances.
   *
   * Note: for the spinner color you must use only solid colors!
   */
  styleParams?: StyleParams;
}

const cssVars = createCssVars(['spinnerColor', 'spinnerSize']);

const root = css`
  ${createAttributeCssVarDefinitions('data-color', {
    options: colorOptions,
    varContract: { spinnerColor: null },
    values: {
      blue: { spinnerColor: color.textBrand },
      grey: {
        // Can use the `textSubdued` token if/when legacy theme is removed.
        // The `textSubdued` is a transparent color on legacy theme, so it didn't achieve
        // the desired effect on the spinner due to overlapping grey elements.
        spinnerColor: {
          light: '#707173',
          lightHighContrast: '#4d4d51',
          dark: '#A3A3A3',
        },
      },
    },
  })}

  ${createAttributeCssVarDefinitions('data-size', {
    options: sizeOptions,
    varContract: { spinnerSize: null },
    values: {
      tiny: { spinnerSize: '14px' },
      small: { spinnerSize: '18px' },
      medium: { spinnerSize: '24px' },
      large: { spinnerSize: '44px' },
      extraLarge: { spinnerSize: '64px' },
      huge: { spinnerSize: '96px' },
    },
  })}

  overflow: hidden;
  position: relative;
  width: ${cssVars.spinnerSize};
  height: ${cssVars.spinnerSize};
  display: inline-block;
  color: ${cssVars.spinnerColor};
`;

const animationContainer = css`
  position: absolute;
  inset: 0;
  animation: mainRotation 2.5s infinite linear;

  @keyframes mainRotation {
    100% {
      transform: rotate(360deg);
    }
  }
`;

const circle = css`
  &,
  svg {
    position: absolute;
    inset: 0;
  }

  circle {
    stroke: currentColor;
    transform-origin: center;
  }
`;

const container1 = css`
  circle {
    stroke-opacity: 0.2;
  }
`;

const container2 = css`
  animation: circle1Rotation 2.2s infinite cubic-bezier(0.45, 0.05, 0.55, 0.95);

  circle {
    stroke-dasharray: 50, 150;
    stroke-dashoffset: 0;
    stroke-linecap: round;
  }

  @keyframes circle1Rotation {
    0% {
      transform: rotate(50deg);
    }
    100% {
      transform: rotate(770deg);
    }
  }
`;

const container3 = css`
  animation: circle2Rotation 2.2s infinite linear;

  circle {
    stroke-dasharray: 45, 150;
    stroke-dashoffset: 0;
    stroke-linecap: round;
  }

  @keyframes circle2Rotation {
    0% {
      transform: rotate(0);
    }
    100% {
      transform: rotate(720deg);
    }
  }
`;

export const Spinner = forwardRef<HTMLDivElement, Props>(
  ({ className, color = 'blue', size, style, styleParams, ...rest }, ref) => {
    return (
      <div
        className={clsx(root, className)}
        data-color={color}
        data-size={size}
        {...rest}
        ref={ref}
        style={
          styleParams
            ? {
                ...style,
                ...assignInlineCssVars(cssVars, {
                  spinnerColor: styleParams.color,
                  spinnerSize: styleParams.size,
                }),
              }
            : style
        }
      >
        <div className={animationContainer}>
          <div className={clsx(circle, container1)}>
            <svg viewBox="0 0 50 50">
              <circle cx="25" cy="25" fill="none" r="20" strokeWidth="5" />
            </svg>
          </div>

          <div className={clsx(circle, container2)}>
            <svg viewBox="0 0 50 50">
              <circle cx="25" cy="25" fill="none" r="20" strokeWidth="5" />
            </svg>
          </div>

          <div className={clsx(circle, container3)}>
            <svg viewBox="0 0 50 50">
              <circle cx="25" cy="25" fill="none" r="20" strokeWidth="5" />
            </svg>
          </div>
        </div>
      </div>
    );
  },
);

Spinner.displayName = 'Spinner';
